import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { apiClient, endpoints } from '../api';
import './Register.css';

const Register = ({ onRegister, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    shopName: '',
    ownerName: '',
    email: '',
    phone: '',
    shopAddress: '',
    city: '',
    password: '',
    confirmPassword: '',
    latitude: '',
    longitude: '',
    acceptTerms: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Real-time validation for specific fields
    if (name === 'email' && value.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        setErrors(prev => ({
          ...prev,
          [name]: 'Please enter a valid email address'
        }));
      }
    }
    
    if (name === 'phone' && value.trim()) {
      const digitsOnly = value.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 10 || digitsOnly.length > 15) {
        setErrors(prev => ({
          ...prev,
          [name]: 'Phone number must have 10-15 digits'
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Shop Name validation
    if (!formData.shopName.trim()) {
      newErrors.shopName = 'Shop name is required';
    } else if (formData.shopName.trim().length < 2) {
      newErrors.shopName = 'Shop name must be at least 2 characters';
    } else if (formData.shopName.trim().length > 100) {
      newErrors.shopName = 'Shop name must be less than 100 characters';
    }

    // Owner Name validation
    if (!formData.ownerName.trim()) {
      newErrors.ownerName = 'Owner name is required';
    } else if (formData.ownerName.trim().length < 2) {
      newErrors.ownerName = 'Owner name must be at least 2 characters';
    } else if (formData.ownerName.trim().length > 100) {
      newErrors.ownerName = 'Owner name must be less than 100 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.ownerName.trim())) {
      newErrors.ownerName = 'Owner name can only contain letters and spaces';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.trim().length > 255) {
      newErrors.email = 'Email must be less than 255 characters';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-\s()]+$/.test(formData.phone.trim())) {
      newErrors.phone = 'Phone number can only contain numbers, +, -, (, ), and spaces';
    } else if (formData.phone.replace(/[^0-9]/g, '').length < 10) {
      newErrors.phone = 'Phone number must have at least 10 digits';
    } else if (formData.phone.replace(/[^0-9]/g, '').length > 15) {
      newErrors.phone = 'Phone number must have at most 15 digits';
    }

    // Shop Address validation
    if (!formData.shopAddress.trim()) {
      newErrors.shopAddress = 'Shop address is required';
    } else if (formData.shopAddress.trim().length < 10) {
      newErrors.shopAddress = 'Shop address must be at least 10 characters';
    } else if (formData.shopAddress.trim().length > 500) {
      newErrors.shopAddress = 'Shop address must be less than 500 characters';
    }

    // City validation
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    } else if (formData.city.trim().length < 2) {
      newErrors.city = 'City must be at least 2 characters';
    } else if (formData.city.trim().length > 100) {
      newErrors.city = 'City must be less than 100 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.city.trim())) {
      newErrors.city = 'City can only contain letters and spaces';
    }

    // Optional coordinates validation (if provided)
    if (formData.latitude) {
      const lat = parseFloat(formData.latitude);
      if (Number.isNaN(lat) || lat < -90 || lat > 90) {
        newErrors.latitude = 'Latitude must be between -90 and 90';
      }
    }
    if (formData.longitude) {
      const lng = parseFloat(formData.longitude);
      if (Number.isNaN(lng) || lng < -180 || lng > 180) {
        newErrors.longitude = 'Longitude must be between -180 and 180';
      }
    }

    // Password validation
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/[A-Za-z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must include letters and numbers';
    }
    if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms acceptance validation
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Make API call to backend
      const response = await apiClient.post(endpoints.registerVendor, {
        shopName: formData.shopName,
        ownerName: formData.ownerName,
        email: formData.email,
        phone: formData.phone,
        shopAddress: formData.shopAddress,
        city: formData.city,
        password: formData.password,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined
      }, {
        timeout: 10000
      });

      if (response.status === 201) {
        // Show success message
        setShowSuccess(true);
        
        // Redirect to login after a short delay
        setTimeout(() => {
          const vendorData = {
            ...formData,
            id: response.data.vendorId,
            status: response.data.status,
            token: response.data.token,
            registrationDate: new Date().toISOString()
          };
          onRegister(vendorData);
        }, 2000);
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        response: error.response,
        request: error.request,
        config: error.config
      });
      
      if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        setErrors({ general: 'Backend server is not running. Please start the backend server first.' });
      } else if (error.response) {
        // Server responded with error status
        console.log('Server response error:', error.response.status, error.response.data);
        if (error.response.status === 400) {
          setErrors({ general: error.response.data.error || 'Invalid data provided' });
        } else if (error.response.status === 409) {
          setErrors({ general: 'Email already exists. Please use a different email.' });
        } else if (error.response.status === 500) {
          setErrors({ general: 'Server error. Please try again later.' });
        } else {
          setErrors({ general: error.response.data.error || 'Registration failed. Please try again.' });
        }
      } else if (error.request) {
        // Request was made but no response received
        console.log('No response received:', error.request);
        setErrors({ general: 'No response from server. Please check if backend is running.' });
      } else {
        // Something else happened
        console.log('Other error:', error.message);
        setErrors({ general: 'Registration failed. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setErrors(prev => ({ ...prev, latitude: 'Geolocation not supported by browser' }));
      return;
    }
    setIsLoading(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
      });
      const { latitude, longitude } = position.coords;
      setFormData(prev => ({ ...prev, latitude: String(latitude), longitude: String(longitude) }));
    } catch (err) {
      setErrors(prev => ({ ...prev, latitude: 'Failed to get current location' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <div className="register-logo">
            <h1>SonicKart</h1>
           
          </div>
           <h2 className="register-subheading">Vendor Portal</h2>
          <p className="register-subtitle">Create your vendor account</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          {errors.general && (
            <div className="error-message general">
              <AlertCircle size={16} />
              <span>{errors.general}</span>
            </div>
          )}

          {showSuccess && (
            <div className="success-message">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                background: '#f0f9ff',
                border: '1px solid #0ea5e9',
                borderRadius: '8px',
                color: '#0369a1',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '16px'
              }}>
                <span>✅</span>
                <span>Registration successful!</span>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="shopName" className="form-label">
                Shop Name *
              </label>
              <div className="input-container">
                <input
                  type="text"
                  id="shopName"
                  name="shopName"
                  value={formData.shopName}
                  onChange={handleChange}
                  className={`form-input ${errors.shopName ? 'error' : ''}`}
                  placeholder="Enter your shop name"
                  disabled={isLoading}
                />
              </div>
              {errors.shopName && (
                <span className="error-text">{errors.shopName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="ownerName" className="form-label">
                Owner Name *
              </label>
              <div className="input-container">
                <input
                  type="text"
                  id="ownerName"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                  className={`form-input ${errors.ownerName ? 'error' : ''}`}
                  placeholder="Enter your full name"
                  disabled={isLoading}
                />
              </div>
              {errors.ownerName && (
                <span className="error-text">{errors.ownerName}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address *
              </label>
              <div className="input-container">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  placeholder="Enter your email"
                  disabled={isLoading}
                />
              </div>
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone Number *
              </label>
              <div className="input-container">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`form-input ${errors.phone ? 'error' : ''}`}
                  placeholder="Enter your phone number"
                  disabled={isLoading}
                />
              </div>
              {errors.phone && (
                <span className="error-text">{errors.phone}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="shopAddress" className="form-label">
              Shop Address *
            </label>
            <div className="input-container">
              <input
                type="text"
                id="shopAddress"
                name="shopAddress"
                value={formData.shopAddress}
                onChange={handleChange}
                className={`form-input ${errors.shopAddress ? 'error' : ''}`}
                placeholder="Enter your shop address"
                disabled={isLoading}
              />
            </div>
            {errors.shopAddress && (
              <span className="error-text">{errors.shopAddress}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="city" className="form-label">
              City *
            </label>
            <div className="input-container">
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={`form-input ${errors.city ? 'error' : ''}`}
                placeholder="Enter your city"
                disabled={isLoading}
              />
            </div>
            {errors.city && (
              <span className="error-text">{errors.city}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="latitude" className="form-label">
                Latitude (optional)
              </label>
              <div className="input-container">
                <input
                  type="number"
                  step="any"
                  id="latitude"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  className={`form-input ${errors.latitude ? 'error' : ''}`}
                  placeholder="e.g. 28.6139"
                  disabled={isLoading}
                />
              </div>
              {errors.latitude && (
                <span className="error-text">{errors.latitude}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="longitude" className="form-label">
                Longitude (optional)
              </label>
              <div className="input-container">
                <input
                  type="number"
                  step="any"
                  id="longitude"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  className={`form-input ${errors.longitude ? 'error' : ''}`}
                  placeholder="e.g. 77.2090"
                  disabled={isLoading}
                />
              </div>
              {errors.longitude && (
                <span className="error-text">{errors.longitude}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <button
              type="button"
              className="register-btn"
              onClick={handleUseCurrentLocation}
              disabled={isLoading}
            >
              Use Current Location
            </button>
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password *
            </label>
            <div className="input-container">
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Create a strong password"
                disabled={isLoading}
              />
            </div>
            {errors.password && (
              <span className="error-text">{errors.password}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password *
            </label>
            <div className="input-container">
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                placeholder="Re-enter your password"
                disabled={isLoading}
              />
            </div>
            {errors.confirmPassword && (
              <span className="error-text">{errors.confirmPassword}</span>
            )}
          </div>

          

          <div className="form-group">
            <div className="checkbox-container" onClick={() => {
              if (!isLoading) {
                setFormData(prev => ({ ...prev, acceptTerms: !prev.acceptTerms }));
                // Clear error when user accepts terms
                if (errors.acceptTerms) {
                  setErrors(prev => ({ ...prev, acceptTerms: '' }));
                }
              }
            }}>
              <div className={`custom-checkbox ${formData.acceptTerms ? 'checked' : ''}`}>
                {formData.acceptTerms && <span className="checkmark-icon">✓</span>}
              </div>
              <span className="terms-text">
                I agree to the <a href="#" className="terms-link" onClick={(e) => e.stopPropagation()}>Terms and Conditions</a> and <a href="#" className="terms-link" onClick={(e) => e.stopPropagation()}>Privacy Policy</a>
              </span>
            </div>
            {errors.acceptTerms && (
              <span className="error-text">{errors.acceptTerms}</span>
            )}
          </div>

          <button
            type="submit"
            className={`register-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading || showSuccess}
          >
            {showSuccess ? (
              'Redirecting to Login...'
            ) : isLoading ? (
              <>
                <div className="spinner"></div>
                Creating Account...
              </>
            ) : (
              'Create Vendor Account'
            )}
          </button>
        </form>

        <div className="register-footer">
          <p>
            Already have an account? 
            <button 
              type="button" 
              className="switch-link"
              onClick={onSwitchToLogin}
            >
              Sign in here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
